import { useEffect, useRef, useState } from "react";
import {
  Alert, AlertIcon, Box, Button, CloseButton, FormControl, FormHelperText, FormLabel, HStack, Image, Input,
  Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text, Textarea, VStack,
} from "@chakra-ui/react";
import { FiFileText, FiPaperclip } from "react-icons/fi";
import { ACCENT, ACCENT_HOVER, SERIF } from "../theme/tokens";
import { STORY_SERIES } from "../lib/storySeries";
import { submitStoryProposal } from "../services/storySubmissionService";

/**
 * "Send us your story": a small card floating on the dashboard that asks
 * members for an article, paper or dataset we could turn into a chapter of
 * "A story in a chart". Dismissing it hides it for three weeks; sending a
 * proposal hides it for three months.
 */

const HIDE_KEY = "story_card_hidden_until";
const DAY = 24 * 60 * 60 * 1000;
const ACCEPT = ".csv,.tsv,.xlsx,.xls,.pdf,.json,.txt,.zip";
const MAX_FILES = 5;
const MAX_MB = 20;

const hiddenNow = () => {
  try { return Number(localStorage.getItem(HIDE_KEY) || 0) > Date.now(); } catch { return false; }
};
const hideFor = (days) => {
  try { localStorage.setItem(HIDE_KEY, String(Date.now() + days * DAY)); } catch { /* storage blocked */ }
};

export function StoryProposalModal({ isOpen, onClose, onSent }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [links, setLinks] = useState("");
  const [files, setFiles] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (isOpen) { setError(""); setSent(false); }
  }, [isOpen]);

  const pick = (list) => {
    const chosen = Array.from(list || []);
    const tooBig = chosen.find((f) => f.size > MAX_MB * 1024 * 1024);
    if (tooBig) { setError(`${tooBig.name} is larger than ${MAX_MB} MB. Send a link to it instead.`); return; }
    setError("");
    setFiles((prev) => [...prev, ...chosen].slice(0, MAX_FILES));
  };

  const send = async () => {
    if (!title.trim()) { setError("Please give your story a title."); return; }
    if (!description.trim() && !links.trim() && !files.length) {
      setError("Add a few words, a link or a file so we know what to look at.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await submitStoryProposal({ title: title.trim(), description, links, files });
      setSent(true);
      setTitle(""); setDescription(""); setLinks(""); setFiles([]);
      if (onSent) onSent();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader pb={1}>
          <Text fontSize="xs" fontWeight="semibold" letterSpacing="0.14em" textTransform="uppercase" color={ACCENT}>
            {STORY_SERIES}
          </Text>
          <Text fontFamily={SERIF} fontSize="2xl">Propose a story</Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {sent ? (
            <VStack align="stretch" spacing={3} py={4}>
              <Alert status="success" borderRadius="md">
                <AlertIcon />
                Thank you. Your proposal has been saved and passed to the editorial team.
              </Alert>
              <Text fontSize="sm" color="gray.600">
                We will look at whether we can build it into a chapter of Visualising Wine, and get in touch if we do.
              </Text>
            </VStack>
          ) : (
            <VStack align="stretch" spacing={4}>
              <Text fontSize="sm" color="gray.600">
                Your paper, an article you wrote, a dataset you know well: send it and we will see whether and how we
                can turn it into a chapter of Visualising Wine.
              </Text>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Title</FormLabel>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200}
                  placeholder="e.g. How Prosecco conquered the British market" />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">What is the story?</FormLabel>
                <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} maxLength={5000}
                  placeholder="What does the data show, and why does it matter? A few lines are enough." />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Links</FormLabel>
                <Textarea value={links} onChange={(e) => setLinks(e.target.value)} rows={3}
                  placeholder={"Your article or paper, the dataset, anything we should read.\nOne link per line."} />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Files</FormLabel>
                <Input ref={fileRef} type="file" multiple accept={ACCEPT} display="none"
                  onChange={(e) => { pick(e.target.files); e.target.value = ""; }} />
                <Button size="sm" variant="outline" leftIcon={<FiPaperclip />} onClick={() => fileRef.current?.click()}
                  isDisabled={files.length >= MAX_FILES}>
                  Attach data or a paper
                </Button>
                <FormHelperText fontSize="xs">CSV, Excel, PDF, JSON or ZIP. Up to {MAX_FILES} files, {MAX_MB} MB each.</FormHelperText>
                {files.length > 0 && (
                  <VStack align="stretch" spacing={1} mt={2}>
                    {files.map((f, i) => (
                      <HStack key={`${f.name}-${i}`} fontSize="sm" justify="space-between" bg="surface.subtle" px={3} py={1.5} borderRadius="md">
                        <HStack spacing={2} minW={0}>
                          <Box as={FiFileText} flexShrink={0} />
                          <Text noOfLines={1}>{f.name}</Text>
                          <Text color="text.muted" flexShrink={0}>{Math.max(1, Math.round(f.size / 1024))} KB</Text>
                        </HStack>
                        <CloseButton size="sm" onClick={() => setFiles(files.filter((_, j) => j !== i))} aria-label={`Remove ${f.name}`} />
                      </HStack>
                    ))}
                  </VStack>
                )}
              </FormControl>
              {error && <Alert status="error" borderRadius="md" fontSize="sm"><AlertIcon />{error}</Alert>}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          {sent ? (
            <Button onClick={onClose}>Close</Button>
          ) : (
            <HStack>
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button bg={ACCENT} color="white" _hover={{ bg: ACCENT_HOVER }} onClick={send} isLoading={busy}>
                Send proposal
              </Button>
            </HStack>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export function FloatingStoryCard() {
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);

  // Arrive a moment after the dashboard, so it reads as an aside rather than
  // as part of the page loading.
  useEffect(() => {
    if (hiddenNow()) return undefined;
    const t = setTimeout(() => setVisible(true), 1800);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => { hideFor(21); setVisible(false); };

  return (
    <>
      {visible && (
        <Box
          position="fixed" right={{ base: 3, md: 6 }} bottom={{ base: 3, md: 6 }} zIndex={30}
          w={{ base: "calc(100% - 24px)", sm: "330px" }}
          bg="white" borderRadius="ui-lg" borderWidth="1px" borderColor="gwbi.200"
          boxShadow="0 18px 50px rgba(43,35,32,0.22)" overflow="hidden"
          sx={{
            animation: "storyCardIn .5s cubic-bezier(.2,.8,.2,1) both",
            "@keyframes storyCardIn": { from: { opacity: 0, transform: "translateY(16px)" }, to: { opacity: 1, transform: "none" } },
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
          role="complementary" aria-label="Propose a story"
        >
          <Box h="4px" bg={ACCENT} />
          <Box p={4} position="relative">
            <CloseButton size="sm" position="absolute" top={2} right={2} onClick={dismiss} aria-label="Hide for now" />
            <HStack spacing={2} mb={2} pr={6}>
              <Image src="/gwbi_logo_red.png" alt="" h="22px" />
              <Box bg={ACCENT} color="white" borderRadius="full" px={2.5} py={0.5}>
                <Text fontSize="2xs" fontWeight="semibold" letterSpacing="0.12em" textTransform="uppercase">{STORY_SERIES}</Text>
              </Box>
            </HStack>
            <Text fontFamily={SERIF} fontWeight="bold" fontSize="lg" lineHeight="1.2" mb={1.5}>
              Is your research our next chapter?
            </Text>
            <Text fontSize="sm" color="gray.600" mb={3}>
              Send us your article, paper or dataset. We will see whether and how we can turn it into a story in
              Visualising Wine.
            </Text>
            <HStack spacing={2}>
              <Button size="sm" bg={ACCENT} color="white" _hover={{ bg: ACCENT_HOVER }} onClick={() => setOpen(true)}>
                Propose a story
              </Button>
              <Button size="sm" variant="ghost" color="gray.600" onClick={dismiss}>Not now</Button>
            </HStack>
          </Box>
        </Box>
      )}
      <StoryProposalModal
        isOpen={open}
        onClose={() => setOpen(false)}
        onSent={() => { hideFor(90); setVisible(false); }}
      />
    </>
  );
}

export default FloatingStoryCard;
